import {
  type AdminV2EntryAction,
  adminV2EntryActionSchema,
} from '@/admin-v2/entry-actions/entry-action';
import { adminV2ExactIdentitySchema } from '@/admin-v2/foundation/admin-v2-identity';
import {
  type SiteRuntimeIcon,
  siteRuntimeIconSchema,
} from '@/site-runtime/runtime-config-contract';
import { z } from 'zod';
import {
  type AdminV2WorkspaceActionBinding,
  type AdminV2WorkspaceActionConfirmation,
  adminV2WorkspaceActionBindingSchema,
  adminV2WorkspaceActionConfirmationSchema,
} from './workspace-action-slots';

export const adminV2WorkspaceToolbarBuiltinIds = [
  'feedback.positive',
  'feedback.negative',
  'compare',
  'fullscreen',
  'download',
] as const;

export const adminV2WorkspaceToolbarBuiltinIdSchema = z.enum(
  adminV2WorkspaceToolbarBuiltinIds
);

export type AdminV2WorkspaceToolbarBuiltinId = z.infer<
  typeof adminV2WorkspaceToolbarBuiltinIdSchema
>;

export const adminV2WorkspaceToolbarBuiltinIconDefaults: Record<
  AdminV2WorkspaceToolbarBuiltinId,
  SiteRuntimeIcon
> = {
  'feedback.positive': { iconSource: 'app', iconKey: 'thumbs-up' },
  'feedback.negative': { iconSource: 'app', iconKey: 'thumbs-down' },
  compare: { iconSource: 'app', iconKey: 'eye-2' },
  fullscreen: { iconSource: 'app', iconKey: 'maximize' },
  download: { iconSource: 'app', iconKey: 'download' },
};

export const adminV2WorkspaceToolbarItemSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('builtin'),
      id: adminV2WorkspaceToolbarBuiltinIdSchema,
      icon: siteRuntimeIconSchema.optional(),
      binding: adminV2WorkspaceActionBindingSchema.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('action'),
      id: adminV2ExactIdentitySchema,
      icon: siteRuntimeIconSchema,
      binding: adminV2WorkspaceActionBindingSchema,
    })
    .strict()
    .superRefine((item, context) => {
      if (
        adminV2WorkspaceToolbarBuiltinIds.includes(
          item.id as AdminV2WorkspaceToolbarBuiltinId
        )
      ) {
        context.addIssue({
          code: 'custom',
          message: 'Custom Toolbar action IDs cannot use built-in IDs.',
          path: ['id'],
        });
      }
    }),
]);

export const adminV2WorkspaceToolbarSchema = z
  .object({
    items: z.array(adminV2WorkspaceToolbarItemSchema).min(1).max(12),
  })
  .strict()
  .superRefine((toolbar, context) => {
    const seen = new Set<string>();
    toolbar.items.forEach((item, index) => {
      if (seen.has(item.id)) {
        context.addIssue({
          code: 'custom',
          message: `Workspace Toolbar item ${item.id} may only be configured once.`,
          path: ['items', index, 'id'],
        });
      }
      seen.add(item.id);
    });
  });

export type AdminV2WorkspaceToolbarItem = z.infer<
  typeof adminV2WorkspaceToolbarItemSchema
>;

export type AdminV2WorkspaceToolbar = z.infer<
  typeof adminV2WorkspaceToolbarSchema
>;

export type AdminV2ResolvedWorkspaceToolbarItem =
  | {
      kind: 'builtin';
      id: AdminV2WorkspaceToolbarBuiltinId;
      icon: SiteRuntimeIcon;
      action?: AdminV2EntryAction;
      actionId?: string;
      actionSource?: 'existingAction' | 'custom';
      confirmation?: AdminV2WorkspaceActionConfirmation;
    }
  | {
      kind: 'action';
      id: string;
      icon: SiteRuntimeIcon;
      action: AdminV2EntryAction;
      actionId?: string;
      actionSource: 'existingAction' | 'custom';
      confirmation?: AdminV2WorkspaceActionConfirmation;
    };

export const adminV2ResolvedWorkspaceToolbarItemSchema = z.discriminatedUnion(
  'kind',
  [
    z
      .object({
        kind: z.literal('builtin'),
        id: adminV2WorkspaceToolbarBuiltinIdSchema,
        icon: siteRuntimeIconSchema,
        action: adminV2EntryActionSchema.optional(),
        actionId: adminV2ExactIdentitySchema.optional(),
        actionSource: z.enum(['existingAction', 'custom']).optional(),
        confirmation: adminV2WorkspaceActionConfirmationSchema.optional(),
      })
      .strict(),
    z
      .object({
        kind: z.literal('action'),
        id: adminV2ExactIdentitySchema,
        icon: siteRuntimeIconSchema,
        action: adminV2EntryActionSchema,
        actionId: adminV2ExactIdentitySchema.optional(),
        actionSource: z.enum(['existingAction', 'custom']),
        confirmation: adminV2WorkspaceActionConfirmationSchema.optional(),
      })
      .strict(),
  ]
);

export type AdminV2WorkspaceToolbarBinding = {
  binding: AdminV2WorkspaceActionBinding;
};

export function resolveAdminV2WorkspaceToolbarIcon(
  item: Pick<AdminV2WorkspaceToolbarItem, 'kind' | 'id' | 'icon'>
) {
  if (item.icon) return item.icon;
  if (item.kind === 'builtin') {
    return adminV2WorkspaceToolbarBuiltinIconDefaults[
      item.id as AdminV2WorkspaceToolbarBuiltinId
    ];
  }
  return null;
}

export const adminV2WorkspaceToolbarDefaultItems =
  adminV2WorkspaceToolbarBuiltinIds
    .filter((id) => id !== 'compare')
    .map((id) => ({
      kind: 'builtin' as const,
      id,
      icon: adminV2WorkspaceToolbarBuiltinIconDefaults[id],
    }));

export function assertAdminV2WorkspaceToolbarActionShape(
  item: AdminV2WorkspaceToolbarItem
) {
  if (item.kind === 'builtin') {
    if (item.binding?.mode === 'custom') {
      adminV2EntryActionSchema.parse(item.binding.action);
    }
    return;
  }
  if (item.binding.mode === 'custom') {
    adminV2EntryActionSchema.parse(item.binding.action);
  }
}
